/**
 * How it works — the booking steps, the two footnotes, and nothing else.
 *
 * EVERY WORD HERE WAS APPROVED BY BRAEDEN, 2026-10-02
 *   Each step carries the decisions it comes from in a comment above it.
 *   Never add a fact that is not in yedding-brain/BRAND-CONTEXT.md or
 *   yedding-brain/DECISIONS-LEDGER.md. A number may be published only if
 *   one of those two files records it (D-0027).
 *
 * WHAT IS DELIBERATELY NOT HERE
 *   No money section: no management fee, no security deposit, no split
 *   payment, no late fee, no refund policy. The checkout code does not yet
 *   match the decisions, and copy may describe money only as the code does
 *   it today (D-0026). The alcohol fee is left off too, until it is in the
 *   code: see "Alcohol Fee Everywhere It Shows Up" in docs/BACKLOG-LEDGER.md.
 *   The host listing steps are not decided yet (Q-0008).
 *
 * ONE COPY OF THE WORDS
 *   src/components/HowItWorks builds both the visible page and the HowTo
 *   JSON-LD from this file. A step's `text` in the JSON-LD is its
 *   paragraphs and list items joined with spaces, so the two cannot drift.
 *   The footnotes are visible only. They are not steps.
 *
 * See docs/ledgers/aeo/AEO-LEDGER.md, task 11b.
 */

const howItWorks = {
  title: 'How Yedding works',
  // The HowTo node's name. Not shown on the page.
  howToName: 'How to book a Yedding event venue',
  intro:
    'Booking a Yedding event venue takes four steps. Here they are, in order. If you have a question at any step, email support@yedding.com.',
  steps: [
    {
      id: 'find-a-venue',
      name: 'Find a venue',
      // BRAND-CONTEXT.md §2.1 (any vendor), §2.2 ("many", never "all").
      paragraphs: [
        'Browse the event venues on yedding.com. Each listing shows photos of the property, how many guests it holds, and the listed price. Every venue lets you hire any vendor you want, and many are booked for the whole day.'
      ]
    },
    {
      id: 'choose-from-the-listing',
      name: 'Choose from the listing',
      // D-0033. The Airbnb comparison is about how you choose, never about
      // what Yedding is: BRAND-CONTEXT.md §4, "Yedding is not an Airbnb".
      paragraphs: [
        'To keep prices low, Yedding generally does not offer venue tours. In this way, booking with Yedding is like booking on Airbnb or other online marketplaces: you choose your venue from the photos and details on the listing.'
      ],
      footnote: 'tours'
    },
    {
      id: 'book-the-venue',
      name: 'Book the venue',
      // D-0029. The code sets the booking to paid in the checkout hook, with
      // no host approval step.
      paragraphs: [
        'Select Book Now on the venue page, sign in, and pay at checkout. After you pay at checkout, the date is yours. The date is not reserved before then.'
      ],
      footnote: 'rentals'
    },
    {
      id: 'event-insurance',
      name: 'Get event insurance, if your event needs it',
      // D-0019, D-0035 (event insurance tiers), D-0036 (liquor liability),
      // D-0034 (venue owner as additional insured). BRAND-CONTEXT.md §7:
      // Yedding never provides insurance. Venue pages do not show the
      // owner's name (security S3.3b). The owner-name sentence changes when
      // checkout shows the name: billing B4.9.
      paragraphs: [
        'Event insurance protects you if someone is hurt or the property is damaged during your event. Liquor liability insurance protects you if someone who drank at your event causes harm. Yedding does not sell insurance, so you buy your own policies from an insurance company or agent.',
        'Each policy has two amounts. The first is the most it pays for any one incident. The second is the most it pays in total, for all incidents combined. What you need depends on your guest count, and on whether alcohol is served:'
      ],
      list: [
        {
          label: '1 to 50 guests:',
          text: 'you do not need event insurance. If alcohol is served, you need liquor liability of $1 million for any one incident, and $2 million in total.'
        },
        {
          label: '51 to 150 guests:',
          text: 'event insurance of $1 million for any one incident, and $2 million in total. If alcohol is served, you also need liquor liability of $1 million and $2 million.'
        },
        {
          label: '151 to 500 guests:',
          text: 'event insurance of $2 million for any one incident, and $3 million in total. If alcohol is served, you also need liquor liability of $2 million and $3 million.'
        },
        {
          label: 'More than 500 guests:',
          text: 'email support@yedding.com, and Yedding sets the amounts with you.'
        }
      ],
      closing: [
        'Each policy must name the venue owner and Yedding, LLC as "additional insured". This means the policy protects them too. After you book, email support@yedding.com to get the owner\'s name.'
      ]
    }
  ],
  footnotes: [
    {
      id: 'tours',
      marker: '*',
      label: 'Tours.',
      // D-0015 (one free tour in total), D-0024 ($50, credited,
      // non-refundable), D-0018 (requests go to support), D-0033.
      text: 'If you are close to booking and cannot decide between one or two venues, Yedding allows one free in-person tour in total, given by Yedding or by the host. Every tour after the first costs $50. The tour fee is non-refundable, and it is credited toward the venue you book. To set up a tour, email support@yedding.com.'
    },
    {
      id: 'rentals',
      marker: '†',
      label: 'Rentals.',
      // D-0010.
      text: 'Some venues offer items to rent, listed in the venue description. Rentals are not booked through checkout on the website. To rent an item, email support@yedding.com, and Yedding sends you a separate invoice.'
    }
  ]
};

/** A step's full text, in page order. The HowTo JSON-LD uses exactly this. */
export const stepText = (step) =>
  [
    ...step.paragraphs,
    ...(step.list || []).map((item) => `${item.label} ${item.text}`),
    ...(step.closing || [])
  ].join(' ');

export default howItWorks;
